// @ts-nocheck
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Header context for managing unified header state and configurations
const HeaderContext = createContext();

// View configuration constants for all application sections
export const VIEW_CONFIGS = {
  dashboard: {
    id: 'dashboard',
    title: 'Dashboard',
    description: "Here's what's happening with your email marketing today",
    greeting: null, // Uses default time-based greeting
    actions: {
      showAIAssistant: true,
      showNewCampaign: false,
      customActions: []
    },
    responsive: {
      mobileTitle: 'Dashboard',
      mobileDescription: 'Your marketing overview'
    }
  },
  campaigns: {
    id: 'campaigns',
    title: 'Campaigns',
    description: 'Manage and monitor your email marketing campaigns',
    greeting: null,
    actions: {
      showAIAssistant: true,
      showNewCampaign: false,
      customActions: []
    },
    responsive: {
      mobileTitle: 'Campaigns',
      mobileDescription: 'Manage campaigns'
    }
  },
  'ab-testing': {
    id: 'ab-testing',
    title: 'A/B Testing',
    description: 'Create and analyze A/B tests for optimal performance',
    greeting: null,
    actions: {
      showAIAssistant: true,
      showNewCampaign: false,
      customActions: []
    },
    responsive: {
      mobileTitle: 'A/B Tests',
      mobileDescription: 'Test & analyze'
    }
  },
  audiences: {
    id: 'audiences',
    title: 'Audiences',
    description: 'Create and manage audience segments for targeted campaigns',
    greeting: null,
    actions: {
      showAIAssistant: true,
      showNewCampaign: false,
      customActions: []
    },
    responsive: {
      mobileTitle: 'Audiences',
      mobileDescription: 'Manage segments'
    }
  },
  leads: {
    id: 'leads',
    title: 'Leads',
    description: 'Track and manage your leads and prospects',
    greeting: null,
    actions: {
      showAIAssistant: true,
      showNewCampaign: false,
      customActions: []
    },
    responsive: {
      mobileTitle: 'Leads',
      mobileDescription: 'Track prospects'
    }
  },
  templates: {
    id: 'templates',
    title: 'Templates',
    description: 'Design and manage email templates for your campaigns',
    greeting: null,
    actions: {
      showAIAssistant: true,
      showNewCampaign: false,
      customActions: []
    },
    responsive: {
      mobileTitle: 'Templates',
      mobileDescription: 'Design templates'
    }
  },
  support: {
    id: 'support',
    title: 'Support',
    description: 'Access help resources and manage support tickets',
    greeting: null,
    actions: {
      showAIAssistant: false,
      showNewCampaign: false,
      customActions: []
    },
    responsive: {
      mobileTitle: 'Support',
      mobileDescription: 'Get help'
    }
  },
  payments: {
    id: 'payments',
    title: 'Payments',
    description: 'Manage billing, subscriptions, and payment methods',
    greeting: null,
    actions: {
      showAIAssistant: false,
      showNewCampaign: false,
      customActions: []
    },
    responsive: {
      mobileTitle: 'Payments',
      mobileDescription: 'Billing & payments'
    }
  },
  settings: {
    id: 'settings',
    title: 'Settings',
    description: 'Configure your account and application preferences',
    greeting: null,
    actions: {
      showAIAssistant: false,
      showNewCampaign: false,
      customActions: []
    },
    responsive: {
      mobileTitle: 'Settings',
      mobileDescription: 'Account settings'
    }
  }
};

// Default view configuration fallback
const DEFAULT_VIEW_CONFIG = {
  id: 'default',
  title: 'Application',
  description: 'Welcome to your marketing platform',
  greeting: null,
  actions: {
    showAIAssistant: false,
    showNewCampaign: false,
    customActions: []
  },
  responsive: {
    mobileTitle: 'App',
    mobileDescription: 'Marketing platform'
  }
};

// Route to view ID mapping for automatic view detection
const ROUTE_TO_VIEW_MAP = {
  '/dashboard': 'dashboard',
  '/campaigns': 'campaigns',
  '/ab-testing': 'ab-testing',
  '/audiences': 'audiences',
  '/leads': 'leads',
  '/templates': 'templates',
  '/support': 'support',
  '/payments': 'payments',
  '/settings': 'settings',
  '/help': 'settings',
  '/integrations': 'settings',
  '/profile': 'settings'
};

// Helper function to extract view ID from pathname
const getViewFromPath = (pathname) => {
  // Direct match first
  if (ROUTE_TO_VIEW_MAP[pathname]) {
    return ROUTE_TO_VIEW_MAP[pathname];
  }

  // Check for parent route (handles nested routes like /campaigns/new)
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0) {
    const parentPath = `/${segments[0]}`;
    if (ROUTE_TO_VIEW_MAP[parentPath]) {
      return ROUTE_TO_VIEW_MAP[parentPath];
    }
  }

  // Default to dashboard
  return 'dashboard';
};

export const HeaderProvider = ({ children }) => {
  const location = useLocation();
  const [currentView, setCurrentView] = useState('dashboard');
  const [viewConfig, setViewConfig] = useState(VIEW_CONFIGS.dashboard);
  const [customOverrides, setCustomOverrides] = useState({});

  // Automatic route-based view detection
  useEffect(() => {
    const pathname = location.pathname;
    const viewId = getViewFromPath(pathname);

    if (viewId !== currentView) {
      const newConfig = VIEW_CONFIGS[viewId] || DEFAULT_VIEW_CONFIG;
      setCurrentView(viewId);
      setViewConfig({ ...newConfig, ...customOverrides });
    }
  }, [location.pathname, currentView, customOverrides]);

  // Update current view and its configuration
  const updateView = useCallback((viewId) => {
    const newConfig = VIEW_CONFIGS[viewId] || DEFAULT_VIEW_CONFIG;
    setCurrentView(viewId);
    setViewConfig({ ...newConfig, ...customOverrides });
  }, [customOverrides]);

  // Update view configuration with custom overrides
  const updateViewConfig = useCallback((config) => {
    setViewConfig(prevConfig => ({
      ...prevConfig,
      ...config
    }));
  }, []);

  // Update title only
  const updateTitle = useCallback((title) => {
    setViewConfig(prevConfig => ({
      ...prevConfig,
      title
    }));
  }, []);

  // Update description only
  const updateDescription = useCallback((description) => {
    setViewConfig(prevConfig => ({
      ...prevConfig,
      description
    }));
  }, []);

  // Set custom overrides that persist across view changes
  const setCustomOverridesCallback = useCallback((overrides) => {
    setCustomOverrides(overrides);
    setViewConfig(prevConfig => ({
      ...prevConfig,
      ...overrides
    }));
  }, []);

  // Get configuration for a specific view without changing current view
  const getViewConfig = useCallback((viewId) => {
    return VIEW_CONFIGS[viewId] || DEFAULT_VIEW_CONFIG;
  }, []);

  // Check if a view has specific actions
  const hasCustomActions = useCallback((viewId = currentView) => {
    const config = VIEW_CONFIGS[viewId] || DEFAULT_VIEW_CONFIG;
    return config.actions.customActions.length > 0;
  }, [currentView]);

  // Get filtered actions based on user permissions
  const getFilteredActions = useCallback((userPermissions = []) => {
    return viewConfig.actions.customActions.filter(action => {
      if (!action.permissions || action.permissions.length === 0) {
        return true;
      }
      return action.permissions.some(permission =>
        userPermissions.includes(permission)
      );
    });
  }, [viewConfig.actions.customActions]);

  // Reset to default configuration
  const resetViewConfig = useCallback(() => {
    const defaultConfig = VIEW_CONFIGS[currentView] || DEFAULT_VIEW_CONFIG;
    setViewConfig(defaultConfig);
    setCustomOverrides({});
  }, [currentView]);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo(() => ({
    // Current state
    currentView,
    viewConfig,
    customOverrides,

    // View management
    updateView,
    updateViewConfig,
    updateTitle,
    updateDescription,
    setCustomOverrides: setCustomOverridesCallback,
    resetViewConfig,

    // Utility functions
    getViewConfig,
    hasCustomActions,
    getFilteredActions,

    // Constants
    VIEW_CONFIGS,
    DEFAULT_VIEW_CONFIG
  }), [
    currentView,
    viewConfig,
    customOverrides,
    updateView,
    updateViewConfig,
    updateTitle,
    updateDescription,
    setCustomOverridesCallback,
    resetViewConfig,
    getViewConfig,
    hasCustomActions,
    getFilteredActions
  ]);

  return (
    <HeaderContext.Provider value={value}>
      {children}
    </HeaderContext.Provider>
  );
};

export const useHeader = () => {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
};

export default HeaderContext;